import { type DocumentNode, useQuery } from '@apollo/client';
import { useConversationMessageMediaContentState } from '@modules/conversation/ConversationMediaContentView/useConversationMessageMediaContent';
import { isSameDay, parseISO } from 'date-fns';
import { useMemo, useRef } from 'react';
import {
  ConversationMessageContentType,
  IConversationMessage,
} from 'src/@types/conversation';
import { graphql } from 'src/generated';
import type {
  TableRowComment,
  TableRowCommentPageInfo,
} from 'src/generated/graphql.js';
import { useConversationContext } from 'src/provider/ConversationContext.js';

export const TableRowActivitiesDoc = graphql(`
   query TableRowActivities($input: QueryTableRowCommentInput!) {
    tableRowComments(input: $input) {
      items {
        ...tableRowComment
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`);

export const useRowActivitiesQuery = ({
  boardId,
  tableId,
  rowId,
}: {
  boardId: string;
  tableId: string;
  rowId: string;
}) => {
  const { goToMessage } = useConversationContext();
  const isFetchingNextPage = useRef<boolean>(false);
  const isFetchingPreviousPage = useRef<boolean>(false);
  const { checkInitialMediaContentDownload } =
    useConversationMessageMediaContentState();

  const { data, loading, fetchMore } = useQuery(
    TableRowActivitiesDoc as DocumentNode,
    {
      variables: {
        input: {
          boardId,
          rowId,
          tableId,
          around: goToMessage,
        },
      },
    },
  );

  const messages = useMemo(() => {
    if (!data?.tableRowComments?.items?.length) return [];
    const sortedItems = [...data.tableRowComments.items].sort(
      (a: TableRowComment, b: TableRowComment) =>
        a.createdAt.localeCompare(b.createdAt),
    );
    return sortedItems.map((message: TableRowComment, index: number) => {
      const { contentType, author, createdAt, mediaThumbnailRef } = message;

      const previousMessage = sortedItems[index - 1];

      const createdAtIso = parseISO(createdAt);
      const isSameDayAsPreviousMessage =
        previousMessage &&
        isSameDay(parseISO(previousMessage?.createdAt), createdAtIso);

      const isSameAuthorAsPreviousMessage =
        isSameDayAsPreviousMessage && previousMessage?.author === author;

      const conversationMessages = message as unknown as IConversationMessage;
      if (
        conversationMessages.contentType ===
          ConversationMessageContentType.VIDEO &&
        !!mediaThumbnailRef
      ) {
        checkInitialMediaContentDownload(
          conversationMessages,
          'mediaThumbnailRef',
        );
      }

      checkInitialMediaContentDownload(conversationMessages);

      return {
        ...message,
        rowId,
        tableId,
        contentType:
          (contentType?.toString() as ConversationMessageContentType) ||
          ConversationMessageContentType.TEXT,
        author,
        createdAt,
        mediaThumbnailRef,
        isSameDayAsPreviousMessage,
        isSameAuthorAsPreviousMessage,
      };
    });
  }, [
    data?.tableRowComments,
    checkInitialMediaContentDownload,
    tableId,
    rowId,
  ]);

  const pageInfo = useMemo(() => {
    if (!data?.tableRowComments?.pageInfo)
      return {
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: null,
        endCursor: null,
      } as TableRowCommentPageInfo;
    return data?.tableRowComments.pageInfo;
  }, [data?.tableRowComments]) as TableRowCommentPageInfo;

  const fetchNextPage = async () => {
    const { endCursor, hasNextPage } = data.tableRowComments.pageInfo;
    if (!hasNextPage) return;
    isFetchingNextPage.current = true;
    await fetchMore({
      variables: {
        input: {
          boardId,
          rowId,
          tableId,
          before: endCursor,
        },
      },
      updateQuery: (prevResult, { fetchMoreResult }) => {
        fetchMoreResult.tableRowComments.items = [
          ...fetchMoreResult.tableRowComments.items,
          ...prevResult.tableRowComments.items,
        ];
        return fetchMoreResult;
      },
    });
    isFetchingNextPage.current = false;
  };
  const fetchPreviousPage = async () => {
    const { startCursor, hasPreviousPage } = data.tableRowComments.pageInfo;
    if (!hasPreviousPage) return;
    isFetchingPreviousPage.current = true;
    await fetchMore({
      variables: {
        input: {
          boardId,
          rowId,
          tableId,
          before: startCursor,
        },
      },
      updateQuery: (prevResult, { fetchMoreResult }) => {
        fetchMoreResult.tableRowComments.items = [
          ...prevResult.tableRowComments.items,
          ...fetchMoreResult.tableRowComments.items,
        ];
        return fetchMoreResult;
      },
    });
    isFetchingPreviousPage.current = false;
  };

  return {
    messages,
    pageInfo,
    isLoading: loading,
    fetchNextPage,
    fetchPreviousPage,
    isFetchingNextPage: isFetchingNextPage.current,
    isFetchingPreviousPage: isFetchingPreviousPage.current,
  };
};
